import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ServiceSchedule } from '@app/core/api-azure-functions/dataverse-functions';
import { SupportPublicService } from '@app/core/api/api/support-public.service';
import { CaseModel } from '@app/core/models/case.model';
import { OptionModel } from '@app/core/models/option.model';
import { SiteModel } from '@app/core/models/site.model';
import { UserDataModel } from '@app/core/models/UserDataModel';
import { FileService } from '@app/core/services/file.service';
import { CustomSnackbarComponent } from '@app/custom-snackbar/custom-snackbar.component';
import {
  CASE_ORIGIN_CODE,
  CUSTOMER_SUB_CASE_TYPES,
  REPORTING_SUB_CASE_TYPES,
  SUPPORT_TYPES,
} from '@app/shared/app.constants';
import { SupportTicketSubmitModalComponent } from '@app/shared/components/support-ticket-submit-modal/support-ticket-submit-modal.component';
import {
  AuthSelectors,
  CaseActions,
  CaseSelectors,
  GlobalActions,
  RouterSelectors,
  SiteActions,
  SiteSelectors,
  UserDataSelectors,
} from '@app/store';
import { AppState } from '@app/store/app.state';
import { AccountInfo } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
} from 'rxjs/operators';

@Component({
  selector: 'app-support-ticket',
  templateUrl: './support-ticket.component.html',
  styleUrls: ['./support-ticket.component.scss'],
})
export class SupportTicketComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  isSitesLoading$: Observable<boolean> = this.store.select(
    SiteSelectors.isSitesLoading
  );
  isAllServiceSchedulesLoading$: Observable<boolean> = this.store.select(
    SiteSelectors.isAllServiceSchedulesLoading
  );
  userinfo$ = this.store.select(UserDataSelectors.getUserInfo);

  todayDate: string = new Date().toISOString();
  siteNameDropdownOptions: { value: string; text: string }[] = [];
  user: UserDataModel;
  supportCaseTypes: OptionModel[];
  onDemandProducts: { value: string; text: string }[];
  wasteStreamDropdownOptions: { value: string; text: string }[];
  equipmentTypeDropdownOptions: {
    value: string;
    text: string;
    options: string[];
  }[];
  equipmentSizeDropdownOptions: { value: string; text: string }[] = [];
  allServiceSchedules: ServiceSchedule[] = [];
  allActiveSites: SiteModel[];
  siteNameGUIDToAddressDictionary: Map<string, string> = new Map();
  attachmentDetails: any[] = [];
  disableSubmit = false;
  formSupportType: any; // the form type (Dynamics Case Type ID)
  filteredOptions: Observable<{ text: string; value: string }[]>;
  customerId: string = null;
  isSmsNotificationEnabled: boolean = false;
  isEmailNotificationEnabled: boolean = false;
  readonly CUSTOMER_SUB_CASE_TYPES = CUSTOMER_SUB_CASE_TYPES;
  readonly SUPPORT_TYPES = SUPPORT_TYPES;
  readonly REPORTING_SUB_CASE_TYPES = REPORTING_SUB_CASE_TYPES;

  ticketForm = this.fb.group({
    subType: [CUSTOMER_SUB_CASE_TYPES.INVOICE_REQUEST],
    subTypeReporting: [REPORTING_SUB_CASE_TYPES.NEW_REPORT_REQUEST],
    siteId: ['', Validators.required],
    address: [{ value: '', disabled: true }],
    existingWasteStream: ['', Validators.required], // first form to load, required
    newWasteStream: [''],
    equipmentType: [undefined, Validators.required],
    equipmentSize: [{ value: undefined, disabled: true }, Validators.required],
    missedDate: [''],
    requestedDropOffDate: [''],
    requestedPickupDate: [''],
    name: [{ value: '', disabled: true }],
    phone: ['', Validators.required],
    additionalEmailAddress: ['', [Validators.maxLength(255)]],
    email: [{ value: '', disabled: true }],
    description: ['', Validators.required],
    fileUpload: [null],
    submit: [''],
  });

  constructor(
    private store: Store<AppState>,
    public modal: MatDialog,
    private datePipe: DatePipe,
    private fb: UntypedFormBuilder,
    private fileService: FileService,
    private supportPublicService: SupportPublicService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit() {
    this.setServiceReqOrSupportTicket();
    this.getSupportCaseTypes();
    this.getSites();
    this.getServiceSchedules();
    this.getUserInfo();
    this.getCustomerId();

    this.watchSiteNameValueChange();
    this.watchExistingWasteStreamValueChange();
    this.watchEquipmentTypeValueChanges();

    this.subscribeToCreatedCase();
  }

  openCustomSnackbar(
    message: string,
    actions: { action: () => void; actionLabel: string; color?: string }[]
  ) {
    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        message: message,
        actions: actions,
      },
      // duration: 5000,  // optional, auto-dismiss after 5 seconds
    });
  }

  handleGotoSettings() {
    this.router.navigate(['/account/account-information'], {
      queryParams: { active: 1 },
    });
  }

  handleClose() {
    //
  }

  ngOnDestroy() {
    this.snackBar.dismiss();
    this.subscriptions.unsubscribe();
  }

  /// ///////////////////////////////////////////////////////////

  // get variables saved in the router to set the forms initial state
  // set default form type (support ticket or service request)
  // initial Support Ticket type = Missed Service
  // initial Service Request form type = Service Request
  setServiceReqOrSupportTicket() {
    this.store
      .select(RouterSelectors.getRouterState)
      .pipe(take(1)) // we only need the initial value
      .subscribe((state: any) => {
        this.getOnDemandProducts();

        if (state?.data?.isServiceRequest) {
          this.changeSupportType(SUPPORT_TYPES.SERVICE_REQUEST);
          // both of the below are only for service requests
          // this.watchExistingWasteStreamValueChange();
        } else {
          this.changeSupportType(SUPPORT_TYPES.MISSED_SERVICE);
        }
      });
  }

  changeSupportType(supportTypeId) {
    this.formSupportType = supportTypeId;

    const requiredcontrolsToClear = [
      this.ticketForm.controls.siteId,
      this.ticketForm.controls.address,
      this.ticketForm.controls.description,
    ];

    // controls to clear
    const controlsToClear = [
      this.ticketForm.controls.existingWasteStream,
      this.ticketForm.controls.equipmentType,
      this.ticketForm.controls.equipmentSize,
      this.ticketForm.controls.missedDate,
    ];

    // Reset Controls
    this.requiredValidators(requiredcontrolsToClear);
    this.clearValidators(controlsToClear);

    // Set Controls validity
    switch (supportTypeId) {
      case SUPPORT_TYPES.SERVICE_REQUEST:
        this.setServiceRequestValidators();
        break;
      case SUPPORT_TYPES.MISSED_SERVICE:
        this.setMissedServiceValidators();
        break;
      case SUPPORT_TYPES.DAMAGED_EQUIPMENT:
        this.setDamagedEquipmentValidators();
        break;
      case SUPPORT_TYPES.INCIDENTS_ACCIDENTS:
        this.setIncidentsAccidentsValidators();
        break;
    }
    this.updateFormValidity(requiredcontrolsToClear);
    this.updateFormValidity(controlsToClear);
  }

  private setServiceRequestValidators() {
    this.requiredValidators([this.ticketForm.controls.requestedPickupDate]);
  }

  private setMissedServiceValidators() {
    this.requiredValidators([
      this.ticketForm.controls.existingWasteStream,
      this.ticketForm.controls.missedDate,
    ]);
  }

  private setDamagedEquipmentValidators() {
    this.requiredValidators([
      this.ticketForm.controls.existingWasteStream,
      this.ticketForm.controls.equipmentType,
      this.ticketForm.controls.equipmentSize,
      // this.ticketForm.controls.fileUpload,
    ]);
    this.clearValidators([this.ticketForm.controls.fileUpload]);
  }

  private setIncidentsAccidentsValidators() {
    this.requiredValidators([this.ticketForm.controls.fileUpload]);
  }

  /// Get data ///////////////////////////////
  getSupportCaseTypes() {
    this.store.dispatch(CaseActions.getCaseTypes());
    const supportTypeSubscription = this.store
      .select(CaseSelectors.getOrderedSupportCaseTypes)
      .subscribe((supportCaseTypes: OptionModel[]) => {
        if (supportCaseTypes) {
          this.supportCaseTypes = supportCaseTypes;
        }
      });

    this.subscriptions.add(supportTypeSubscription);
  }

  getSites() {
    const sitesSubscription = this.store
      .select(SiteSelectors.getAllActiveSites)
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe((allActiveSites: SiteModel[]) => {
        if (allActiveSites !== undefined) {
          allActiveSites.forEach(site => {
            this.siteNameDropdownOptions.push({
              value: site.accountId,
              text: site.name,
            });
            this.siteNameGUIDToAddressDictionary.set(
              site.accountId,
              site.address1 +
                ', ' +
                site.city +
                ', ' +
                site.state +
                ' ' +
                site.zip
            );
          });
          // save all site data for later
          this.allActiveSites = allActiveSites;

          this.ticketForm.controls.siteId.setValue('');
        }
      });

    this.subscriptions.add(sitesSubscription);
  }

  getServiceSchedules() {
    const serviceScheduleSubscription = this.store
      .select(SiteSelectors.getAllActiveServiceSchedules)
      .pipe(filter(response => response !== null && response.length > 0))
      .subscribe((allServices: ServiceSchedule[]) => {
        this.allServiceSchedules = allServices;
      });

    this.subscriptions.add(serviceScheduleSubscription);
  }

  getUserInfo() {
    this.subscriptions.add(
      this.store
        .select(AuthSelectors.getUser)
        .subscribe((user: AccountInfo) => {
          if (user !== null) {
            this.ticketForm.controls.email.setValue(user.idTokenClaims.email);
            this.ticketForm.controls.name.setValue(user.name);
          }
        })
    );

    this.subscriptions.add(
      this.userinfo$.subscribe((user: UserDataModel) => {
        if (user !== null && user.adObjectId) {
          this.user = user;
          // Set phone number
          this.ticketForm.controls.phone.setValue(user.phoneNumber);

          const notifications = this.user?.notificationSettings;
          const caseUpdatesNotifications = notifications?.caseUpdates;

          // Is SMS or Email notifications are enabled
          this.isSmsNotificationEnabled = !!(
            this.user?.phoneNumber &&
            this.user?.isPhoneValidated &&
            caseUpdatesNotifications.sms
          );

          this.isEmailNotificationEnabled = !!caseUpdatesNotifications?.email;

          const isValidNotificationSet =
            this.isSmsNotificationEnabled || this.isEmailNotificationEnabled;

          // Get notification type
          const notificationType = (() => {
            if (
              this.isSmsNotificationEnabled &&
              this.isEmailNotificationEnabled
            ) {
              return 'SMS and Email';
            }
            if (this.isSmsNotificationEnabled) {
              return 'SMS';
            }
            if (this.isEmailNotificationEnabled) {
              return 'Email';
            }
            return 'N/A';
          })();

          this.openCustomSnackbar(
            isValidNotificationSet
              ? `You will receive notifications via ${notificationType} for updates related to this case.`
              : `Want to receive case updates via SMS, Email ? Opt in for notifications.`,
            isValidNotificationSet
              ? [
                  {
                    action: () => this.handleClose(),
                    actionLabel: 'Close',
                    color: 'accent',
                  },
                ]
              : [
                  {
                    action: () => this.handleGotoSettings(),
                    actionLabel: 'Set Notifications',
                    color: 'accent',
                  },
                  {
                    action: () => this.handleClose(),
                    actionLabel: 'Close',
                    color: 'warn',
                  },
                ]
          );
        }
      })
    );
  }

  private getCustomerId() {
    const tenantSubscription = this.store
      .select(AuthSelectors.getUserIds)
      .pipe(
        filter(userIdCheck => !!userIdCheck) // filter out nulls
      )
      .subscribe(userIds => {
        this.customerId = userIds.accountId;
      });

    this.subscriptions.add(tenantSubscription);
  }

  getOnDemandProducts() {
    this.store.dispatch(SiteActions.getOnDemandProducts());
    const onDemandProductsSubscription = this.store
      .select(SiteSelectors.getOnDemandProducts)
      .pipe(filter(valueCheck => !!valueCheck)) // filter out no values
      .subscribe(
        (onDemandProducts: { value: string; text: string }[]) =>
          (this.onDemandProducts = onDemandProducts)
      );

    this.subscriptions.add(onDemandProductsSubscription);
  }

  /// Site name change ////////////////////////
  private updateWasteStreamEquipmentValidationRules() {
    const equipmentControls = [
      this.ticketForm.controls.equipmentType,
      this.ticketForm.controls.equipmentSize,
    ];

    // If Waste Stream and Equipment Type and Size Controls Available (request, missed and damaged type)
    if (this.isWasteStreamEquipmentControlAvailable()) {
      this.requiredValidators([this.ticketForm.controls.existingWasteStream]);

      // If there are service scedules then equipment type and size are not available
      if (this.serviceSchedulesForSelectedSite.length) {
        this.clearEquipmentValidators();
      } else {
        this.requiredValidators(equipmentControls);
      }
    } else {
      this.clearValidators([this.ticketForm.controls.existingWasteStream]);
      this.clearEquipmentValidators();
    }

    this.updateFormValidity([
      this.ticketForm.controls.existingWasteStream,
      ...equipmentControls,
    ]);
  }

  private updateDropdownOptionsWithSchedules() {
    this.serviceSchedulesForSelectedSite.forEach(service => {
      this.wasteStreamDropdownOptions.push({
        value: service.scheduleId,
        text: `${service.debris.toString()} - ${service.accountServiceEquipment.toString()} ${
          service?.serviceDetails ? '-' + service?.serviceDetails : ''
        }`,
      });
    });

    this.wasteStreamDropdownOptions.push({ value: 'Other', text: 'Other' });

    this.wasteStreamDropdownOptions.unshift({
      value: '',
      text: 'Select Waste Stream',
    });
  }

  private updateDropdownOptionsWithoutSchedules() {
    this.wasteStreamDropdownOptions =
      this.supportPublicService.getWasteStreamOptions();

    const foundOtherIndex = this.wasteStreamDropdownOptions.findIndex(
      item => item.value === 'Other'
    );

    if (foundOtherIndex === -1) {
      this.wasteStreamDropdownOptions.push({ value: 'Other', text: 'Other' });
    }

    this.equipmentTypeDropdownOptions =
      this.supportPublicService.getEquipmentTypeOptions();
  }

  private updateWasteStreamDropdown() {
    this.wasteStreamDropdownOptions = [];

    if (this.serviceSchedulesForSelectedSite.length) {
      this.updateDropdownOptionsWithSchedules();
      this.updateWasteStreamEquipmentValidationRules();
    } else {
      this.store
        .select(SiteSelectors.getAllActiveServiceSchedules)
        .pipe(debounceTime(400))
        .pipe(filter(response => response !== null && response.length > 0))
        .subscribe(() => {
          if (this.serviceSchedulesForSelectedSite.length) {
            this.updateDropdownOptionsWithSchedules();
          } else {
            this.updateDropdownOptionsWithoutSchedules();
          }
          this.updateWasteStreamEquipmentValidationRules();
        });
    }
  }

  // eslint-disable-next-line no-underscore-dangle
  private _filter(value: string): { text: string; value: string }[] {
    const filterValue = value.toLowerCase();

    return this.siteNameDropdownOptions.filter(option =>
      option.text.toLowerCase().includes(filterValue)
    );
  }

  displayFn(option: { text: string; value: string }): string {
    return option && option.text ? option.text : '';
  }

  private handleSiteIdChange(option: { text: string; value: string }) {
    // make sure a site is selected before continuing
    if (option && option.value && option.value.length) {
      // set address
      const siteAddress = this.siteNameGUIDToAddressDictionary.get(
        option.value
      );

      this.ticketForm.controls.address.setValue(siteAddress);
      this.updateWasteStreamDropdown();
    } else {
      // no site selected, reset address
      this.ticketForm.controls.address.setValue('');
    }
  }

  isInvalidSiteValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const controlValue = control.value;
      // check if controlValue is not empty and equal to one of the option
      const index =
        typeof controlValue === 'object' && controlValue.value !== ''
          ? this.siteNameDropdownOptions.findIndex(
              option => option.value === controlValue.value
            )
          : -1;

      return index < 0
        ? {
            inValidSite: {
              value:
                controlValue === 'object' ? controlValue.value : controlValue,
            },
          }
        : null;
    };
  }

  watchSiteNameValueChange() {
    this.filteredOptions = this.ticketForm.controls.siteId.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : value.text)),
      map(text =>
        // eslint-disable-next-line no-underscore-dangle
        text ? this._filter(text) : this.siteNameDropdownOptions.slice()
      )
    );

    const siteNameSubscription = this.ticketForm
      .get('siteId')
      .valueChanges.pipe(debounceTime(400))
      .subscribe((option: { text: string; value: string }) => {
        this.handleSiteIdChange(option);
      });

    this.ticketForm.controls.siteId.setValidators(
      this.isInvalidSiteValidator()
    );

    this.subscriptions.add(siteNameSubscription);
  }

  get serviceSchedulesForSelectedSite() {
    const currentSiteId = this.ticketForm.controls.siteId.value.value;

    return this.allServiceSchedules.filter(
      service => service.accountId === currentSiteId
    );
  }

  /// Existing Waste Stream change ///////////////
  private handleOtherOption(value: string) {
    if (value === 'Other') {
      if (this.isServiceRequest()) {
        this.requiredValidators([
          this.ticketForm.controls.requestedDropOffDate,
        ]);
      }
      this.requiredValidators([this.ticketForm.controls.newWasteStream]);
      this.clearEquipmentValidators();
    } else {
      if (this.isServiceRequest()) {
        this.clearValidators([this.ticketForm.controls.requestedDropOffDate]);
      }
      this.clearValidators([this.ticketForm.controls.newWasteStream]);

      if (this.serviceSchedulesForSelectedSite.length) {
        this.clearEquipmentValidators();
      } else {
        this.requiredValidators([
          this.ticketForm.controls.equipmentType,
          this.ticketForm.controls.equipmentSize,
        ]);
      }
    }

    this.updateFormValidity([
      this.ticketForm.controls.requestedDropOffDate,
      this.ticketForm.controls.newWasteStream,
      this.ticketForm.controls.equipmentType,
      this.ticketForm.controls.equipmentSize,
    ]);
  }

  watchExistingWasteStreamValueChange() {
    const existingWasteStreamSubscription = this.ticketForm
      .get('existingWasteStream')
      .valueChanges.pipe(filter(value => !!value))
      .subscribe(value => {
        this.handleOtherOption(value);
      });

    this.subscriptions.add(existingWasteStreamSubscription);
  }

  /// Equipment Type change /////////////////////
  watchEquipmentTypeValueChanges() {
    const equipmentTypeSubscription =
      this.ticketForm.controls.equipmentType.valueChanges.subscribe(
        selectedValue => {
          this.ticketForm.controls.equipmentSize.reset();

          if (!selectedValue) {
            this.disableEquipmentSize();
          } else {
            this.enableAndPopulateEquipmentSize(selectedValue);
          }
        }
      );

    this.subscriptions.add(equipmentTypeSubscription);
  }

  /// on create case Success ////////////////////
  subscribeToCreatedCase() {
    const createdCaseSubscription = this.store
      .select(CaseSelectors.getCreatedCaseNumber)
      .subscribe(createdCaseNumber => {
        if (createdCaseNumber) {
          // hide spinner when new caseNumber is returned
          this.store.dispatch(GlobalActions.hideSpinner());
          this.modal.open(SupportTicketSubmitModalComponent, {
            disableClose: true,
            data: {
              caseNumber: createdCaseNumber,
              rerouteUrl: '/support-signed-in/support-ticket-list',
            },
          });
        }
      });

    this.subscriptions.add(createdCaseSubscription);
  }

  /// ////////////// Validators ///////////////////
  private clearValidators(controls: AbstractControl[]) {
    controls.forEach(control => control.setValidators([]));
  }

  private requiredValidators(controls: AbstractControl[]) {
    controls.forEach(control => control.setValidators([Validators.required]));
  }

  private updateFormValidity(
    controls: AbstractControl[],
    clearValue: boolean = true
  ) {
    if (clearValue) {
      // Clear Value
      controls.forEach(control => control.setValue(''));
    }

    controls.forEach(control => control.updateValueAndValidity());
  }

  private disableEquipmentSize() {
    this.ticketForm.controls.equipmentSize.disable();
    this.equipmentSizeDropdownOptions = [];
  }

  private enableAndPopulateEquipmentSize(selectedValue: string) {
    this.ticketForm.controls.equipmentSize.enable();

    const foundSelectedEquipmentSizeOption =
      this.equipmentTypeDropdownOptions.find(
        equipmentTypeDropdownOption =>
          equipmentTypeDropdownOption.value === selectedValue
      );

    if (foundSelectedEquipmentSizeOption) {
      this.equipmentSizeDropdownOptions =
        foundSelectedEquipmentSizeOption.options.map(str => ({
          text: str,
          value: str,
        }));

      // If there is only one size option, select the size directly
      if (this.equipmentSizeDropdownOptions.length === 1) {
        this.ticketForm.controls.equipmentSize.setValue(
          this.equipmentSizeDropdownOptions[0].value
        );
      }
    }
  }

  private clearEquipmentValidators() {
    this.clearValidators([
      this.ticketForm.controls.equipmentType,
      this.ticketForm.controls.equipmentSize,
    ]);
  }

  private isWasteStreamEquipmentControlAvailable(): boolean {
    // There are no schedules available +
    // The support type is either of Damaged Equip, Missed service or service Request

    return [
      SUPPORT_TYPES.SERVICE_REQUEST,
      SUPPORT_TYPES.MISSED_SERVICE,
      SUPPORT_TYPES.DAMAGED_EQUIPMENT,
    ].includes(this.formSupportType);
  }

  public isExistingWasteStreamVisible(): boolean {
    return (
      this.ticketForm.controls.siteId?.value?.value &&
      this.isWasteStreamEquipmentControlAvailable()
    );
  }

  public isNewWasteStreamVisible(): boolean {
    return (
      this.ticketForm.controls.existingWasteStream.value &&
      this.ticketForm.controls.existingWasteStream.value === 'Other' &&
      this.isWasteStreamEquipmentControlAvailable()
    );
  }

  public isEquipmentTypeVisible(): boolean {
    return (
      !this.serviceSchedulesForSelectedSite.length &&
      this.ticketForm.controls.existingWasteStream.value &&
      this.ticketForm.controls.existingWasteStream.value !== 'Other' &&
      this.isWasteStreamEquipmentControlAvailable()
    );
  }

  public isEquipmentSizeVisible(): boolean {
    return (
      !this.serviceSchedulesForSelectedSite.length &&
      this.ticketForm.controls.equipmentType.value &&
      this.isWasteStreamEquipmentControlAvailable()
    );
  }

  /// File upload /////////////////////////////////
  setFiles(files: File[]) {
    this.attachmentDetails = [];

    if (files && files.length > 0) {
      // Disable the submit button while processing files
      this.disableSubmit = true;
      // Store base64 strings for all files
      const fileUploadPromises = files.map(file =>
        this.fileService.getBase64(file).then((result: string) => {
          // Extract only the base64 string

          this.attachmentDetails.push({
            attachmentFileName: file.name,
            attachmentContentType: file.type,
            attachmentContent: result.split(';base64,')[1],
          });
        })
      );

      // Wait for all files to be processed
      Promise.all(fileUploadPromises).then(() => {
        // Set all files and their base64 strings to the form control
        this.ticketForm.controls.fileUpload.setValue(files);

        // Enable the submit button after processing
        this.disableSubmit = false;
      });
    } else {
      this.ticketForm.controls.fileUpload.setValue(null);
      this.requiredValidators([this.ticketForm.controls.fileUpload]);
      this.ticketForm.controls.fileUpload.updateValueAndValidity();
    }
  }

  /// on Submit /////////////////////////////////
  private markAllFieldsAsTouched() {
    Object.keys(this.ticketForm.controls).forEach(field => {
      const control = this.ticketForm.get(field);

      control.markAsTouched({ onlySelf: true });
    });
  }

  private getSelectedSite(): SiteModel {
    return this.allActiveSites.find(
      site => site.accountId === this.ticketForm.controls.siteId.value.value
    );
  }

  private getSelectedServiceSchedule(): ServiceSchedule {
    return this.allServiceSchedules.find(
      schedule =>
        schedule.scheduleId ===
        this.ticketForm.controls.existingWasteStream.value
    );
  }

  private getCaseTypeName(): string {
    if (this.isServiceRequest()) {
      return this.ticketForm.controls.existingWasteStream.value === 'Other'
        ? 'On Demand'
        : 'On Call';
    }

    const caseType = this.supportCaseTypes.find(
      supportCaseType => supportCaseType.id === this.formSupportType
    );

    return caseType?.displayName || caseType?.name || '';
  }

  private buildBasicDescription(): string {
    return `Name: ${this.ticketForm.controls.name.value}
      ${
        this.ticketForm.controls.email.value
          ? 'Email:' + this.ticketForm.controls.email.value
          : ''
      }      
      Phone: ${this.ticketForm.controls.phone.value}`;
  }

  private isServiceRequest(): boolean {
    return this.formSupportType === SUPPORT_TYPES.SERVICE_REQUEST;
  }

  private isMissedServiceOrDamagedEquipment(): boolean {
    return [
      SUPPORT_TYPES.MISSED_SERVICE,
      SUPPORT_TYPES.DAMAGED_EQUIPMENT,
    ].includes(this.formSupportType);
  }

  private buildMissedServiceOrDamagedEquipmentDescription(
    wasteStream: string,
    equipmentType: string,
    equipmentSize: string | undefined = undefined
  ): string {
    return `Waste Stream: ${wasteStream}${
      equipmentType ? ' - ' + equipmentType : ''
    }${equipmentSize ? ' - ' + equipmentSize : ''} `;
  }

  private buildDropOffPickupDateDescription(
    dropOffDate: string,
    pickupDate: string
  ): string {
    const dropOff = this.datePipe.transform(
      new Date(dropOffDate),
      'MM/dd/yyyy'
    );
    const pickup = this.datePipe.transform(new Date(pickupDate), 'MM/dd/yyyy');

    return `\n
      Requested DropOff Date: ${dropOff || '-'}
      Requested Pickup Date: ${pickup || '-'} `;
  }

  private dateOnCreateCase(): string | null {
    if (this.isServiceRequest()) {
      const dropOff = this.datePipe.transform(
        new Date(this.ticketForm.controls.requestedPickupDate.value),
        'MM/dd/yyyy'
      );

      return dropOff;
    }
    return null;
  }

  private getSubCaseTypeCode(): number | undefined {
    switch (this.formSupportType) {
      case SUPPORT_TYPES.FINANCE:
        return Number(this.ticketForm.controls.subType.value);
      case SUPPORT_TYPES.REPORTING:
        return Number(this.ticketForm.controls.subTypeReporting.value);
      default:
        return undefined;
    }
  }

  onSubmit() {
    if (this.ticketForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    const selectedSite: SiteModel = this.getSelectedSite();
    const selectedServiceSchedule: ServiceSchedule =
      this.getSelectedServiceSchedule();

    const caseTypeName = this.getCaseTypeName();
    const isServiceScheduleSelected =
      selectedServiceSchedule && selectedServiceSchedule.scheduleId;

    let description = this.buildBasicDescription();

    if (this.isMissedServiceOrDamagedEquipment() || this.isServiceRequest()) {
      if (selectedServiceSchedule) {
        description += this.buildMissedServiceOrDamagedEquipmentDescription(
          selectedServiceSchedule.debris.toString(),
          selectedServiceSchedule.accountServiceEquipment.toString()
        );
      } else {
        const wasteStream =
          this.ticketForm.controls.existingWasteStream.value === 'Other'
            ? this.ticketForm.controls.newWasteStream.value.join(', ')
            : this.ticketForm.controls.existingWasteStream.value;

        description += this.buildMissedServiceOrDamagedEquipmentDescription(
          wasteStream,
          this.ticketForm.controls.equipmentType.value,
          this.ticketForm.controls.equipmentSize.value
        );
      }

      if (this.isServiceRequest()) {
        if (this.ticketForm.controls.existingWasteStream.value === 'Other') {
          description += this.buildDropOffPickupDateDescription(
            this.ticketForm.controls.requestedDropOffDate.value,
            this.ticketForm.controls.requestedPickupDate.value
          );
        }
      }
    }

    description += `\n\n${this.ticketForm.controls.description.value}`;

    const newCase: CaseModel = {
      title: `${caseTypeName} - ${selectedSite.name}`,
      caseTypeCode: Number(this.formSupportType),
      subCaseTypeCode: this.getSubCaseTypeCode(),
      description: description,
      accountId: this.ticketForm.controls.siteId.value.value,
      rtsCompanyId: selectedSite.rtsCompanyId,
      caseOriginCode: CASE_ORIGIN_CODE.CUSTOMER_PORTAL,
      usersName: this.ticketForm.controls.name.value,
      phoneNumber: this.ticketForm.controls.phone.value,
      emailAddress: this.ticketForm.controls.email.value,
      additionalEmailAddress: this.isEmailNotificationEnabled
        ? this.ticketForm.controls.additionalEmailAddress.value
        : undefined,
      attachments: this.attachmentDetails,
      scheduleAId: isServiceScheduleSelected
        ? selectedServiceSchedule.scheduleId.toString()
        : '00000000-0000-0000-0000-000000000000',
      wasteStream: isServiceScheduleSelected
        ? undefined
        : this.ticketForm.controls.existingWasteStream.value,
      equipmentType: isServiceScheduleSelected
        ? undefined
        : this.ticketForm.controls.equipmentType.value,
      // eslint-disable-next-line no-nested-ternary
      equipmentSize: isServiceScheduleSelected
        ? undefined
        : this.ticketForm.controls.equipmentSize.value
          ? this.ticketForm.controls.equipmentSize.value
          : undefined,
      customerId: this.customerId,
      rts_requestedservicedate: this.dateOnCreateCase(),
      emailNotification: this.isEmailNotificationEnabled,
      sms: this.isSmsNotificationEnabled,
    };

    this.store.dispatch(GlobalActions.showSpinner());
    this.store.dispatch(CaseActions.createCase({ newCase: newCase }));
  }
}
